import {
  DataGridPro,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridCallbackDetails,
  GridCellEditStopParams,
  GridCellParams,
  GridColDef,
  GridRowClassNameParams,
  GridRowModesModel,
  GridRowsProp,
  GridSortModel,
  GridToolbarContainer,
  GridToolbarExport,
  GridTreeNode,
  MuiEvent,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { ThemeProvider } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { customDataGridResultTheme } from './CustomForecastDataGridUtils';
import { mediaQuery } from './CustomGenericDataGridUtils';
import { CustomNoRowsOverlay } from './CustomRotateMatricesDataGridUtils';
import { ClickAwayListener, Theme } from '@mui/material';
import useSingleClickEditDataGrid from './useSingleClickEditDataGrid';
import { frFR } from '@mui/x-data-grid-pro/locales';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

type CustomForecastDataGridResultParams = {
  rows: GridRowsProp;
  columns: GridColDef[];
  isLoading?: boolean;
  fullSize: boolean;
  theme?: Theme;
  groupingColDef?: object;
  getTreeDataPath?: ((row: any) => string[]) | undefined;
  getRowClassName?: ((params: GridRowClassNameParams<any>) => string) | undefined;
  getCellClassName?: ((params: GridCellParams<any, any, any>) => string) | undefined;
  processRowUpdate?: ((newRow: any, oldRow: any) => any) | undefined;
  sortModel?: GridSortModel | undefined;
  treeData?: boolean | undefined;
  simulationContext?: string;
  fromSimulation?: boolean;
  fromRotateEffects?: boolean;
  onCellEditStop?: ((params: GridCellEditStopParams, event: MuiEvent) => void) | undefined;
  handleRowModesModelChange?:
    | ((rowModesModel: GridRowModesModel, details: GridCallbackDetails) => void)
    | undefined;
  rowModesModel?: undefined | any;
  setRowModesModel?: undefined | React.Dispatch<React.SetStateAction<GridRowModesModel>>;
  fromCapPoints?: boolean;
  isCellEditable?: ((params: GridCellParams<any, any, any, GridTreeNode>) => boolean) | undefined;
  fromKeyNumberNitrogen?: boolean;
  fromCapPointsSimulation?: boolean;
  allowSubmitEffectBrush?: boolean;
  handleBrushEffect?:
    | ((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void)
    | undefined;
  callAPI?: ((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void) | undefined;
  allowSubmitEffect?: boolean;
  fullScreen?: boolean;
  handleFullScreen?: React.MouseEventHandler<SVGSVGElement>;
  handleResetGroundTypeEffects?: (() => void) | undefined;
  fromGroundTypeEffects?: boolean;
  from?: string;
  stackedRequestsRef?: React.RefObject<any[]> | null;
  activeExport?: boolean;
  fromRightPanel?: boolean;
  customToolbar?: ReactElement<any, any>;
  externalApiRef?: any;
  filterPanel?: any;
  isGroupExpandedByDefault?: (node: any) => boolean;
};

const CustomForecastDataGridResult = ({
  rows,
  columns,
  fullSize,
  theme = undefined,
  groupingColDef = undefined,
  getTreeDataPath = undefined,
  getRowClassName = undefined,
  getCellClassName = undefined,
  isLoading = false,
  processRowUpdate = undefined,
  sortModel = undefined,
  treeData = false,
  simulationContext = '',
  fromSimulation = false,
  fromRotateEffects = false,
  onCellEditStop = undefined,
  handleRowModesModelChange = undefined,
  rowModesModel = undefined,
  setRowModesModel = undefined,
  fromCapPoints = false,
  isCellEditable = undefined,
  fromKeyNumberNitrogen = false,
  fromCapPointsSimulation = false,
  stackedRequestsRef = null,
  activeExport = false,
  fromRightPanel = false,
  customToolbar,
  externalApiRef,
  filterPanel,
  isGroupExpandedByDefault = undefined,
}: CustomForecastDataGridResultParams) => {
  const { t } = useTranslation();
  const internalApiRef = useGridApiRef();
  const apiRef = externalApiRef || internalApiRef;
  const { cellModesModel, handleCellClick, handleCellModesModelChange } =
    useSingleClickEditDataGrid(stackedRequestsRef);

  const shouldShowCustomNoRowsOverlay = () => {
    if (fromRotateEffects || fromRightPanel) {
      return rows.length === 0;
    }
  };

  //change toolbar texte to FR and change filter label to "lignes"
  const customLocaleText = {
    ...frFR.components.MuiDataGrid.defaultProps.localeText,
    toolbarFilters: t('common.rows'),
    toolbarExport: t('common.export'),
  };

  const closeRowAfterScrollbarClick = () => {
    for (const key in rowModesModel) {
      if (
        rowModesModel[key]?.mode === 'edit' &&
        rowModesModel[key].hasOwnProperty('fromScrollBar')
      ) {
        apiRef.current?.stopCellEditMode({ id: key, field: rowModesModel[key].fieldToFocus });
        if (setRowModesModel !== undefined) {
          setRowModesModel({});
        }
      }
    }
  };

  const CustomToolbar = () => {
    return (
      <GridToolbarContainer>
        {activeExport && (
          <GridToolbarExport
            csvOptions={{
              utf8WithBom: true,
            }}
          />
        )}
      </GridToolbarContainer>
    );
  };
  return (
    <ClickAwayListener onClickAway={closeRowAfterScrollbarClick}>
      <Box
        sx={{
          height: 'auto',
        }}
        className={simulationContext ? simulationContext : 'box'}
      >
        <ThemeProvider theme={theme ?? customDataGridResultTheme}>
          <DataGridPro
            localeText={customLocaleText}
            sx={{
              '& .MuiDataGrid-virtualScroller': {
                overflowX: fromSimulation || fromRotateEffects ? 'auto !important' : '',
                maxHeight: !fullSize
                  ? '400px'
                  : fromRotateEffects
                    ? '70vh'
                    : fromSimulation || fromCapPointsSimulation
                      ? 'none'
                      : '100vh',
              },
              '& .MuiDataGrid-row:not([aria-level="1"]) .key-number-campain': {
                paddingLeft: '30px !important',
              },
              '& .MuiDataGrid-cell': {
                display: fromCapPoints ? 'block' : '',
                lineHeight: fromCapPoints ? '2.5 !important' : '',
              },
              '& .MuiDataGrid-treeDataGroupingCellToggle .MuiIconButton-root': {
                padding: '0px',
              },
              '& .MuiDataGrid-overlayWrapper': {
                height: fromRotateEffects && shouldShowCustomNoRowsOverlay() ? '100%' : '',
                '& .MuiDataGrid-overlayWrapperInner': {
                  alignContent:
                    fromRotateEffects && shouldShowCustomNoRowsOverlay() ? 'center' : '',
                },
              },
            }}
            rows={rows}
            onCellEditStop={(params, event) => {
              if (fromRotateEffects) {
                if (onCellEditStop !== undefined) {
                  onCellEditStop(params, event);
                }
              }
            }}
            apiRef={apiRef}
            onRowClick={closeRowAfterScrollbarClick}
            onRowModesModelChange={handleRowModesModelChange}
            columns={columns}
            columnHeaderHeight={fromRotateEffects ? 80 : 56}
            getRowClassName={getRowClassName}
            getCellClassName={getCellClassName}
            cellModesModel={cellModesModel}
            onCellModesModelChange={handleCellModesModelChange}
            onCellClick={handleCellClick}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={(error) => console.log(error)}
            loading={isLoading}
            disableColumnMenu
            hideFooter
            // autoHeight={fromKeyNumberNitrogen || fromCapPointsSimulation ? true : false}
            sortModel={sortModel}
            treeData={treeData}
            getTreeDataPath={getTreeDataPath}
            groupingColDef={groupingColDef}
            disableColumnResize={true}
            disableColumnReorder={true}
            defaultGroupingExpansionDepth={fromKeyNumberNitrogen ? 0 : -1}
            isGroupExpandedByDefault={isGroupExpandedByDefault}
            initialState={{
              pinnedColumns: {
                left: fromSimulation
                  ? [GRID_CHECKBOX_SELECTION_COL_DEF.field, 'Parcelle']
                  : fromRotateEffects
                    ? [GRID_CHECKBOX_SELECTION_COL_DEF.field, 'cropToPlant']
                    : [],
              },
            }}
            getEstimatedRowHeight={() => 54}
            getRowHeight={({ model }) => {
              if (fromRotateEffects && model && model.cropToPlant) {
                return 62;
              }
              if (fromSimulation) {
                if (model?.isExpanded) {
                  return 80 + (model.childrenCount || 0) * 24;
                }
                if (
                  Object.values(model)
                    .filter((value) => value === Object(value))
                    .some((value) => value.isParcelDivision === true)
                ) {
                  return 'auto';
                }
                return 80;
              }
              if (fromCapPoints) {
                return 42;
              }
              if (fromKeyNumberNitrogen) {
                if (model.hierarchy.length > 1) {
                  return 52;
                }
                return 70;
              }
              return 52;
            }}
            slots={{
              ...(shouldShowCustomNoRowsOverlay() && { noRowsOverlay: CustomNoRowsOverlay }),
              toolbar: customToolbar ? () => customToolbar : undefined,
              ...(filterPanel && { filterPanel: filterPanel }),
            }}
            showToolbar={customToolbar ? true : false}
            isCellEditable={isCellEditable}
          />
        </ThemeProvider>
      </Box>
    </ClickAwayListener>
  );
};

export default CustomForecastDataGridResult;
